/* eslint-disable react-hooks/exhaustive-deps */
import {useNavigate, useParams} from "react-router-dom";
import React, {useCallback, useEffect, useState} from "react";
import styles from "./Styles.module.scss";
import {Box, Breadcrumbs, Container, Grid, Link as BCLink, MenuItem, Select, FormControl, InputLabel, Chip} from "@mui/material";
import {NavigateNext} from "@mui/icons-material";
import axios from "../../axios";
import {numberFormatter} from "../../formatter";
import Quantity from "../../components/Quantity";
import MiniLoading from "../../components/MiniLoading";
import {useTranslation} from "react-i18next";
import Auth from "../../Auth";

const formatUnit = (u) => {
  if (!u) return "";
  const desc = u.unit_description || u.unit_code || "";
  const factor = u.number_of_items_per_unit;
  return factor ? `${desc} — ${factor} unit${factor !== 1 ? "s" : ""} per ${desc}` : desc;
};

const Product = () => {
  const urlParams = useParams();
  const productID = urlParams.productID;
  const navigate = useNavigate();
  const [imageIndex, setImageIndex] = useState(0);
  const [revImgArr, setRevImgArr] = useState([]);
  const [bgPosition, setBgPosition] = useState("50% 50%");
  const [product, setProduct] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);

  useEffect(() => {
    fetchProduct(productID).then(function (res) {
      setProduct(res);
      setRevImgArr([res.PictureUrl]);

      if (res.available_units?.length > 0) {
        const units = res.available_units.map((u) => ({ ...u, unit_code: u.unit_code?.trim() }));
        // Pre-select the item's default unit, fall back to first
        const defaultUnit =
          units.find((u) => u.unit_code === res.Unit?.trim()) ?? units[0];
        setSelectedUnit(defaultUnit);
      }
    });
  }, []);

  const fetchProduct = async (id) => {
    return await new Promise(function (resolve, reject) {
      axios
        .get("/products/" + id)
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((err) => {
          navigate("/");
          reject(err);
        });
    });
  };

  const handleClick = (event) => {
    event.preventDefault();
    navigate(event.target.pathname);
  };

  const changeMainImage = useCallback((event) => {
    const selectedImgIndex = Number(
      event.currentTarget.getAttribute("imgindex")
    );
    setImageIndex(selectedImgIndex);
  }, []);

  const zoom = (e) => {
    const {left, top, width, height} = e.target.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setBgPosition(`${x}% ${y}%`);
  };

  const hasMultipleUnits = product?.available_units?.length > 1;
  const hasSingleUnit = product?.available_units?.length === 1;

  // Price shown reflects the selected unit when one is chosen
  const displayPrice = selectedUnit ? selectedUnit.price : product?.price;
  const displayOriginalPrice = selectedUnit ? selectedUnit.original_price : product?.original_price;

  const {t} = useTranslation();

  return (
    <div>
      <Container sx={{pb: "1rem"}}>
        <div role="presentation" onClick={handleClick}>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={<NavigateNext fontSize="small"/>}
            sx={{p: "14px 0"}}
          >
            <BCLink
              className={styles.brdHov}
              underline="none"
              color="inherit"
              href="/"
              fontSize={"14px"}
            >
              {t("Home")}
            </BCLink>
            <BCLink
              className={styles.brdHov}
              underline="none"
              color="inherit"
              href="/products"
              fontSize={"14px"}
            >
              {t("Products")}
            </BCLink>
            <BCLink
              underline="none"
              color="text.primary"
              href={window.location.href}
              aria-current="page"
              fontSize={"14px"}
            >
              {product == null ? "-" : product.Description}
            </BCLink>
          </Breadcrumbs>
        </div>
        {product !== null ? (
          <Grid
            container
            spacing={2}
            sx={{justifyContent: "center"}}
          >
            <Grid item md={6} className={styles.left}>
              <div className={styles.images}>
                {revImgArr.map((item, index) => (
                  <img
                    onClick={changeMainImage}
                    imgindex={index}
                    className={
                      index === imageIndex
                        ? styles.activeImage
                        : styles.notActive
                    }
                    src={item}
                    key={index}
                    alt={"images"}
                  />
                ))}
              </div>
              <div
                className={styles.mainImage}
                onMouseMove={zoom}
                style={{
                  backgroundImage: `url(${revImgArr[imageIndex]})`,
                  backgroundPosition: bgPosition,
                }}
              >
                <img
                  src={revImgArr[imageIndex]}
                  alt="mainImage"
                />
              </div>
            </Grid>

            <Grid item md={6}>
              <div className={styles.right}>
                <p
                  style={{margin: 0, padding: 0}}
                  className={styles.description}
                >
                  {t("SKU")}: <b>{product.Code ?? "-"}</b>
                  <br/>
                  {t("Barcode")}:{" "}
                  <b>{product.Barcode ?? "-"}</b>
                </p>
                <h1 style={{margin: 0, padding: 0}}>
                  {product.Description}
                </h1>
                <p className={styles.price}>
                  {numberFormatter.format(displayPrice)}
                </p>
                <p className={styles.description}>
                  {product.ExtraDescription}
                </p>

                {/* Unit selector — dropdown for multiple units, static label for single unit */}
                {hasMultipleUnits && (
                  <Box sx={{marginBottom: 2, width: "60%"}}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t("Unit")}</InputLabel>
                      <Select
                        value={selectedUnit?.unit_code ?? ""}
                        label={t("Unit")}
                        onChange={(e) => {
                          const unit = product.available_units.find(
                            (u) => u.unit_code?.trim() === e.target.value
                          );
                          setSelectedUnit(unit ? { ...unit, unit_code: unit.unit_code?.trim() } : null);
                        }}
                      >
                        {product.available_units.map((u) => (
                          <MenuItem key={u.unit_code?.trim()} value={u.unit_code?.trim()}>
                            {formatUnit(u)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                {hasSingleUnit && (
                  <Box sx={{marginBottom: 2, fontSize: "0.9rem", color: "#555"}}>
                    {formatUnit(selectedUnit)}
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    width: "30%",
                    marginBottom: 2,
                  }}
                >
                  {/* Block add-to-cart until a unit is selected when required */}
                  {hasMultipleUnits && !selectedUnit ? (
                    <Box sx={{color: "text.secondary", fontSize: "0.85rem", mt: 1}}>
                      {t("Please select a unit to add to cart.")}
                    </Box>
                  ) : (
                    <Quantity
                      id={product.ID}
                      sku={product.Code}
                      unitCode={selectedUnit?.unit_code ?? null}
                    />
                  )}
                </Box>
                <div className={styles.end}>
                  <p>
                    {t("Item Group")}:{" "}
                    {product.ItemGroupCode}
                  </p>
                </div>
                {(() => {
                  const fields = (product.detail_extra_fields ?? []).filter(
                    (f) => f.value !== null && f.value !== undefined && f.value !== ""
                  );
                  if (fields.length === 0) return null;
                  const highlights = fields.filter((f) => f.highlight);
                  const specs = fields.filter((f) => !f.highlight);
                  return (
                    <Box sx={{ mt: 2 }}>
                      {/* Highlighted fields — attention callouts */}
                      {highlights.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: specs.length ? 2.5 : 0 }}>
                          {highlights.map((f) => (
                            <Box
                              key={f.number}
                              sx={{
                                display: "inline-flex",
                                flexDirection: "column",
                                minWidth: 110,
                                px: 1.75,
                                py: 1,
                                borderRadius: 1.5,
                                bgcolor: "#fff8e1",
                                border: "1px solid #ffca28",
                              }}
                            >
                              <span style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.5, color: "#8a6d00" }}>
                                {f.label}
                              </span>
                              <strong style={{ fontSize: "1.05rem", color: "#5d4a00", marginTop: 2 }}>{f.value}</strong>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Specifications table */}
                      {specs.length > 0 && (
                        <Box>
                          <p style={{ fontWeight: 700, fontSize: "1rem", margin: "0 0 8px", color: "#222" }}>
                            {t("Specifications")}
                          </p>
                          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                            <tbody>
                              {specs.map((f, i) => (
                                <tr key={f.number} style={{ background: i % 2 ? "#fafafa" : "transparent" }}>
                                  <td style={{ padding: "9px 12px", color: "#666", width: "45%", borderBottom: "1px solid #eee", verticalAlign: "top" }}>
                                    {f.label}
                                  </td>
                                  <td style={{ padding: "9px 12px", fontWeight: 600, color: "#222", borderBottom: "1px solid #eee" }}>
                                    {f.value}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
                {window.storeSettings?.show_stock_numbers && Array.isArray(product.stock_positions) && (() => {
                  const isAdminSession = Auth.getUserType() === 'store' || Auth.isImpersonating();
                  const includedPositions = product.stock_positions.filter((sp) => sp.included !== false);
                  const totalFreeStock = includedPositions.reduce((sum, sp) => sum + Number(sp.FreeStock || 0), 0);
                  const showBreakdown = isAdminSession && window.storeSettings?.show_warehouse_breakdown && product.stock_positions.length > 0;

                  return (
                    <div className={styles.stockPositions}>
                      {/* Customer-visible total — included warehouses only */}
                      <p className={styles.stockTitle}>
                        {t("Available stock")}: <strong>{totalFreeStock}</strong>
                      </p>

                      {/* Per-warehouse breakdown — admin / impersonated only */}
                      {showBreakdown && (
                        <>
                          <p className={styles.stockTitle} style={{ marginTop: 10 }}>
                            {t("Warehouse breakdown")}:
                          </p>
                          <div className={styles.stockList}>
                            {product.stock_positions.map((sp) => (
                              <div
                                key={sp.ID}
                                className={styles.stockRow}
                                style={sp.included === false ? { opacity: 0.45, borderStyle: 'dashed' } : {}}
                              >
                                <span className={styles.stockWarehouse}>
                                  {sp.WarehouseCode} — {sp.WarehouseDescription}
                                </span>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span className={styles.stockValue}>
                                    {t("Free")}: {Number(sp.FreeStock)}
                                  </span>
                                  {sp.included === false && (
                                    <Chip label={t("Excluded")} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                                  )}
                                </Box>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            </Grid>
          </Grid>
        ) : (
          <MiniLoading/>
        )}
      </Container>
    </div>
  );
};

export default React.memo(Product);
